import React, { useEffect, useState } from 'react';
import { Folder } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const MoveWorkflowFolderModal = ({
  isOpen,
  onClose,
  workflow,
  categories = [],
  folders = [],
  onMove,
  moving = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedCategory(workflow?.category || '');
    setSelectedFolderId(workflow?.folder_id || '');
  }, [isOpen, workflow]);

  const handleMove = () => {
    onMove?.({
      category: selectedCategory || undefined,
      folderId: selectedFolderId || null,
    });
  };

  const hasCategories = categories.length > 0;
  const hasFolders = folders.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        className="max-w-md"
        data-testid="move-workflow-folder-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder size={18} />
            Move to Folder
          </DialogTitle>
          <DialogDescription>
            Choose a category or folder for &quot;{workflow?.name || 'this workflow'}&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {hasCategories ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category</p>
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    data-testid={`move-folder-category-${category.id}`}
                    onClick={() => setSelectedCategory(category.id)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      selectedCategory === category.id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 hover:bg-slate-50',
                    )}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {hasFolders ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Custom folders</p>
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    data-testid={`move-folder-custom-${folder.id}`}
                    onClick={() => setSelectedFolderId(folder.id)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      selectedFolderId === folder.id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 hover:bg-slate-50',
                    )}
                  >
                    {folder.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {!hasCategories && !hasFolders ? (
            <p className="text-sm text-slate-500">Folders are not configured yet.</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={moving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleMove}
            disabled={moving || (!hasCategories && !hasFolders)}
            data-testid="move-workflow-folder-confirm"
          >
            {moving ? 'Moving...' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveWorkflowFolderModal;
